import { AVATAR_ZOOM_MAX, AVATAR_ZOOM_MIN, DEFAULT_AVATAR_CROP, avatarCropRect, normalizeAvatarCrop, type AvatarCrop } from "@nemo/agent-schema";
import { useCallback, useEffect, useRef, useState } from "react";
import { Api, ApiError, type AvatarInfo } from "../api.js";
import { useI18n } from "../i18n.js";

const TYPES = ["image/jpeg", "image/png", "image/webp"];
export const MAX_BYTES = 5 * 1024 * 1024;
/** The sizes the picture appears at in the chat: bubble, header and the smallest use. */
const PREVIEWS = [56, 38, 32] as const;
const FRAME = 240;

/** The picture inside a circle, positioned with the same formula the server uses to cut it. */
export function CropCircle({ src, width, height, crop, size, label }: { src: string; width: number; height: number; crop: AvatarCrop; size: number; label?: string }) {
  const r = avatarCropRect(width, height, crop);
  const scale = size / r.size;
  return (
    <span className="crop-circle" style={{ width: size, height: size }} role={label ? "img" : undefined} aria-label={label} aria-hidden={label ? undefined : true}>
      <img src={src} alt="" draggable={false} style={{ width: width * scale, height: height * scale, transform: `translate(${-r.left * scale}px, ${-r.top * scale}px)` }} />
    </span>
  );
}

/** Keeps the square inside the picture, and remembers where its middle really is. */
const settled = (w: number, h: number, c: AvatarCrop): AvatarCrop => {
  const n = normalizeAvatarCrop(c);
  const r = avatarCropRect(w, h, n);
  return { zoom: n.zoom, x: (r.left + r.size / 2) / w, y: (r.top + r.size / 2) / h };
};

interface Editing {
  src: string;
  width: number;
  height: number;
  /** A new file to upload; null when adjusting the picture that is already stored. */
  file: File | null;
  crop: AvatarCrop;
}

type Phase = { kind: "idle" } | { kind: "editing"; e: Editing } | { kind: "saving"; e: Editing };

const loadImage = (src: string) =>
  new Promise<{ width: number; height: number }>((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
    img.onerror = () => reject(new Error("unreadable"));
    img.src = src;
  });

/**
 * Upload a picture from the computer or phone, put it in a round frame (drag, zoom with the slider, the wheel, two fingers or the
 * keyboard), see it at the sizes the chat uses, and save. `value` and `onChange` are the agent's avatarUrl, so autosave and
 * publishing work as for every other field.
 */
export function AvatarEditor({ api, agentId, name, value, onChange }: { api: Api; agentId: string; name: string; value: string | undefined; onChange: (url: string | undefined) => void }) {
  const { t } = useI18n();
  const [phase, setPhase] = useState<Phase>({ kind: "idle" });
  const [stored, setStored] = useState<AvatarInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const input = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let live = true;
    api.avatar(agentId).then((a) => live && setStored(a), () => {});
    return () => {
      live = false;
    };
  }, [api, agentId]);

  const ours = !!stored && value === stored.url;
  const fail = (e: unknown) => {
    const code = e instanceof ApiError ? e.code : "";
    const known = ["too_large", "too_many_pixels", "unsupported_type", "animated", "too_small", "corrupt", "no_file", "rate_limited", "no_source", "busy"];
    setError(known.includes(code) ? t(`avatar.err.${code}` as never) : e instanceof ApiError && e.status === 0 ? t("common.network") : t("avatar.err.generic"));
  };

  const choose = async (file: File | undefined) => {
    setError(null);
    setNotice("");
    if (!file) return;
    if (!TYPES.includes(file.type)) return setError(t("avatar.err.unsupported_type"));
    if (file.size > MAX_BYTES) return setError(t("avatar.err.too_large"));
    const src = URL.createObjectURL(file);
    try {
      const { width, height } = await loadImage(src);
      if (!width || !height) throw new Error("empty");
      setPhase({ kind: "editing", e: { src, width, height, file, crop: DEFAULT_AVATAR_CROP } });
    } catch {
      URL.revokeObjectURL(src);
      setError(t("avatar.err.corrupt"));
    }
  };

  const adjust = async () => {
    setError(null);
    try {
      const src = await api.avatarSourceUrl(agentId);
      const { width, height } = await loadImage(src);
      setPhase({ kind: "editing", e: { src, width, height, file: null, crop: stored?.crop ?? DEFAULT_AVATAR_CROP } });
    } catch (e) {
      fail(e);
    }
  };

  const close = () => {
    if (phase.kind !== "idle") URL.revokeObjectURL(phase.e.src);
    setPhase({ kind: "idle" });
  };

  const setCrop = useCallback((fn: (c: AvatarCrop) => AvatarCrop) => {
    setPhase((p) => (p.kind === "editing" ? { kind: "editing", e: { ...p.e, crop: settled(p.e.width, p.e.height, fn(p.e.crop)) } } : p));
  }, []);

  const save = async () => {
    if (phase.kind !== "editing") return;
    const e = phase.e;
    setPhase({ kind: "saving", e });
    setError(null);
    try {
      const a = e.file ? await api.uploadAvatar(agentId, e.file, e.crop) : await api.recropAvatar(agentId, e.crop);
      setStored(a);
      onChange(a.url);
      URL.revokeObjectURL(e.src);
      setPhase({ kind: "idle" });
      setNotice(t("avatar.saved"));
    } catch (err) {
      setPhase({ kind: "editing", e });
      fail(err);
    }
  };

  const remove = async () => {
    setError(null);
    onChange(undefined);
    setNotice(t("avatar.removed"));
    try {
      if (stored) await api.removeAvatar(agentId);
      setStored((s) => (s ? { ...s, canAdjust: false } : s));
    } catch {
      /* the draft no longer points at it; the stored copy is cleaned up with the agent */
    }
  };

  const editing = phase.kind === "editing" || phase.kind === "saving" ? phase.e : null;
  const busy = phase.kind === "saving";

  return (
    <div className="avatar-editor">
      <div className="avatar-row">
        <span className="avatar-now" aria-hidden={value ? undefined : true}>
          {value ? <img src={stored && value === stored.url ? stored.smallUrl : value} alt={t("avatar.alt", { name: name || t("avatar.thisAgent") })} /> : <span className="avatar-initial">{(name.trim()[0] ?? "?").toUpperCase()}</span>}
        </span>
        {!editing && (
          <div className="row">
            <button type="button" className="btn secondary" onClick={() => input.current?.click()}>{value ? t("avatar.replace") : t("avatar.upload")}</button>
            {ours && stored?.canAdjust && <button type="button" className="btn secondary" onClick={() => void adjust()}>{t("avatar.adjust")}</button>}
            {value && <button type="button" className="btn secondary" onClick={() => void remove()}>{t("avatar.remove")}</button>}
          </div>
        )}
      </div>

      {!editing && (
        <div
          className={`dropzone ${dragOver ? "over" : ""}`}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            void choose(e.dataTransfer.files[0]);
          }}
        >
          <p>{t("avatar.drop")}</p>
          <p className="hint">{t("avatar.rules")}</p>
        </div>
      )}
      <input ref={input} type="file" accept="image/jpeg,image/png,image/webp" hidden aria-label={t("avatar.fileInput")} onChange={(e) => { void choose(e.target.files?.[0]); e.target.value = ""; }} />

      {editing && <CropPanel e={editing} busy={busy} setCrop={setCrop} onSave={() => void save()} onCancel={close} />}

      <p className="field-error" role="alert" hidden={!error}>{error}</p>
      <p className="hint" aria-live="polite">{notice}</p>
    </div>
  );
}

function CropPanel({ e, busy, setCrop, onSave, onCancel }: { e: Editing; busy: boolean; setCrop: (fn: (c: AvatarCrop) => AvatarCrop) => void; onSave: () => void; onCancel: () => void }) {
  const { t } = useI18n();
  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const pinch = useRef<{ dist: number; zoom: number } | null>(null);
  const rect = avatarCropRect(e.width, e.height, e.crop);

  // dragging the picture moves the square the other way; the picture-pixels per screen-pixel follow the zoom
  const perPx = rect.size / FRAME;
  const move = (dxPx: number, dyPx: number) => setCrop((c) => ({ ...c, x: c.x - (dxPx * perPx) / e.width, y: c.y - (dyPx * perPx) / e.height }));
  const dist = () => {
    const [a, b] = [...pointers.current.values()];
    return a && b ? Math.hypot(a.x - b.x, a.y - b.y) : 0;
  };

  return (
    <div className="crop-panel">
      <div className="crop-layout">
        <div
          className="crop-frame"
          style={{ width: FRAME, height: FRAME }}
          tabIndex={0}
          role="group"
          aria-label={t("avatar.frame")}
          aria-describedby="crop-help"
          onPointerDown={(ev) => {
            ev.currentTarget.setPointerCapture?.(ev.pointerId);
            pointers.current.set(ev.pointerId, { x: ev.clientX, y: ev.clientY });
            if (pointers.current.size === 2) pinch.current = { dist: dist(), zoom: e.crop.zoom };
          }}
          onPointerMove={(ev) => {
            const prev = pointers.current.get(ev.pointerId);
            if (!prev) return;
            pointers.current.set(ev.pointerId, { x: ev.clientX, y: ev.clientY });
            if (pointers.current.size === 2 && pinch.current && pinch.current.dist > 0) {
              const ratio = dist() / pinch.current.dist;
              setCrop((c) => ({ ...c, zoom: pinch.current!.zoom * ratio }));
            } else if (pointers.current.size === 1) move(ev.clientX - prev.x, ev.clientY - prev.y);
          }}
          onPointerUp={(ev) => {
            pointers.current.delete(ev.pointerId);
            pinch.current = null;
          }}
          onPointerCancel={(ev) => {
            pointers.current.delete(ev.pointerId);
            pinch.current = null;
          }}
          onWheel={(ev) => {
            ev.preventDefault?.();
            setCrop((c) => ({ ...c, zoom: c.zoom * (ev.deltaY < 0 ? 1.08 : 1 / 1.08) }));
          }}
          onKeyDown={(ev) => {
            const step = (ev.shiftKey ? 0.1 : 0.02) * rect.size;
            const k = ev.key;
            if (k === "ArrowLeft" || k === "ArrowRight" || k === "ArrowUp" || k === "ArrowDown") {
              ev.preventDefault();
              setCrop((c) => ({ ...c, x: c.x + ((k === "ArrowRight" ? 1 : k === "ArrowLeft" ? -1 : 0) * step) / e.width, y: c.y + ((k === "ArrowDown" ? 1 : k === "ArrowUp" ? -1 : 0) * step) / e.height }));
            } else if (k === "+" || k === "=") {
              ev.preventDefault();
              setCrop((c) => ({ ...c, zoom: c.zoom + 0.1 }));
            } else if (k === "-" || k === "_") {
              ev.preventDefault();
              setCrop((c) => ({ ...c, zoom: c.zoom - 0.1 }));
            }
          }}
        >
          <CropCircle src={e.src} width={e.width} height={e.height} crop={e.crop} size={FRAME} />
        </div>
        <div className="crop-side">
          <label htmlFor="crop-zoom">{t("avatar.zoom")}</label>
          <input id="crop-zoom" type="range" min={AVATAR_ZOOM_MIN} max={AVATAR_ZOOM_MAX} step={0.01} value={e.crop.zoom} onChange={(ev) => setCrop((c) => ({ ...c, zoom: Number(ev.target.value) }))} aria-valuetext={`${Math.round(e.crop.zoom * 100)}%`} />
          <p id="crop-help" className="hint">{t("avatar.help")}</p>
          <p className="hint">{t("avatar.preview")}</p>
          <div className="previews">
            {PREVIEWS.map((s) => (
              <CropCircle key={s} src={e.src} width={e.width} height={e.height} crop={e.crop} size={s} />
            ))}
          </div>
        </div>
      </div>
      <div className="row">
        <button type="button" className="btn primary" onClick={onSave} disabled={busy}>{busy ? t("avatar.saving") : t("avatar.save")}</button>
        <button type="button" className="btn secondary" onClick={onCancel} disabled={busy}>{t("common.cancel")}</button>
      </div>
    </div>
  );
}
